import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import SidebarLayout from './SidebarLayout';
import PackageTypeStep from './PackageTypeStep';
import axios from 'axios';
import { resolveServicePricing } from '../../utils/servicePricing';
import {
  compositionPayload,
  typeById,
  typeNeedsQuantities,
  validateCompositionChoice,
} from '../../utils/packageCompositionCopy';

/**
 * Is this package's VALUE locked? (user, 2026-10-09)
 *
 * A package behaves like a printed gift card: once a professional has subscribed, they have been quoted a
 * figure and accepted the terms, so what it is worth cannot move.
 *
 * READ FROM THE STAMP, NEVER FROM THE LIVE SUBSCRIBER LIST — unsubscribing does not unlock it. The list is
 * the fallback for a package subscribed before the stamp existed.
 *
 * THE SERVER IS THE ENFORCEMENT (`utils/packageValueLock.js`); this only greys the inputs so a shop is not
 * typing into a form that will refuse them at the end. Both read the same two fields, which is why this is
 * ONE function rather than a condition repeated beside every input.
 */
function isValueLocked(pkg) {
  if (!pkg) return false;
  return Boolean(pkg.valueLockedAt || (pkg.subscribedStaffIds || []).length > 0);
}

/**
 * Which of the four plain-language types does a SAVED package correspond to?
 *
 * The model is the authority, not the name: a bundle is `sessionContents === "bundle"`, a shared visit is
 * `consumptionModel === "shared_per_visit"`, and within the quota models the difference between "one service
 * over several visits" and "a set number of each service" is whether there is one service line or several.
 *
 * `null` when nothing matches, which leaves the form's choice empty. That is deliberate: guessing would show
 * the shop a customer sentence its package does not match, and the form refuses an unchosen type rather than
 * saving one.
 */
function inferTypeId(pkg) {
  if (pkg?.sessionContents === 'bundle') return 'bundle';
  if (pkg?.consumptionModel === 'shared_per_visit') return 'couple';
  const lineCount = Array.isArray(pkg?.services) ? pkg.services.length : 0;
  if (lineCount === 1) return 'single';
  if (lineCount > 1) return 'family';
  return null;
}

function Packages() {
  const [packages, setPackages] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [activeOnly, setActiveOnly] = useState(true);
  const [showSubscribersModal, setShowSubscribersModal] = useState(false);
  const [selectedPackageForSubscribers, setSelectedPackageForSubscribers] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [useAutoPrice, setUseAutoPrice] = useState(true);
  const [discountPercent, setDiscountPercent] = useState(20);
  const [initialDiscountSet, setInitialDiscountSet] = useState(false);
  /**
   * SHAREABLE PURCHASE LINK (2026-10-23).
   *
   * `/packages/:packageId/purchase` is already a public route — deliberately outside ProtectedRoute,
   * because a guest buying a package is the point — so the only thing missing was a way for a shop to
   * GET the link. This holds the id whose link was just copied, purely so the button can say "Copied"
   * for a moment instead of silently doing nothing.
   */
  const [copiedPackageId, setCopiedPackageId] = useState(null);

  // R2 may VIEW packages but must not create, edit or delete one — CRUD is owner-only (R1). The server
  // enforces this (`authorizeRoles("R1")` on POST/PATCH/DELETE); hiding the controls here only avoids
  // offering R2 an action that would come back as a 403.
  const isR2 = useMemo(() => {
    const currentRole =
      localStorage.getItem('ogestyle_role_override') || localStorage.getItem('ogestyle_role');
    return currentRole === 'R2';
  }, []);

  // PHASE 17 — SOLD PACKAGES. The shop's own sales are an OWNER view (R1): every row carries a customer's
  // name, email and what they paid. R2 keeps the packages page, but not this tab. The server refuses
  // them (`authorizeRoles("R1")`), because hiding a tab is not enforcement.
  const isR1 = useMemo(() => {
    const currentRole =
      localStorage.getItem('ogestyle_role_override') || localStorage.getItem('ogestyle_role');
    // An unknown/absent role is treated as "not R1": the tab stays hidden and the API decides.
    return currentRole === 'R1';
  }, []);

  const [tab, setTab] = useState('packages'); // 'packages' | 'sold'
  const [sold, setSold] = useState({
    purchases: [],
    counts: null,
    loading: false,
    error: '',
    truncated: false,
  });
  const [soldStatus, setSoldStatus] = useState('all');
  const [soldDetail, setSoldDetail] = useState(null);
  const [soldDetailLoading, setSoldDetailLoading] = useState(false);
  const [pricingSuggestions, setPricingSuggestions] = useState(null);
  const [servicesMissingPrices, setServicesMissingPrices] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'service_bundle',
    // Phase 22A — the composition model. `packageType` is the FORM's plain-language choice (§3.4) and the
    // three `model` fields are what the server validates; `compositionPayload()` derives the latter from the
    // former, so the two can never disagree. Empty `packageType` means the shop has not chosen yet, which the
    // form refuses — a package whose kind is unknown is a package whose customer sentence is unknown.
    packageType: '',
    holderModel: 'single',
    coveredPeople: '',
    regularPrice: '',
    packagePrice: '',
    totalSessions: '',
    validityDays: 90,
    services: [{ serviceId: '', quantity: 1 }]
  });

  // The chosen kind, and the two questions it decides. `typeById` returns null until the shop picks, and
  // every conditional below reads that as "do not ask yet" rather than defaulting to a model.
  const chosenType = typeById(formData.packageType);
  const isBundleForm = chosenType?.model?.sessionContents === 'bundle';
  const oneServiceOnly = chosenType?.serviceLines === 'one';

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.ogestyle.com';
  // Calculate total service quantities
  const calculateTotalServiceQuantity = () => {
    return formData.services.reduce((sum, service) => {
      return sum + (parseInt(service.quantity) || 0);
    }, 0);
  };

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('ogestyle_token');
    if (!token) {
      window.location.href = '/login';
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchData = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      // PHASE 4 FIX: Use my-shops endpoint instead of User.shopIds
      const shopsResponse = await axios.get(`${API_BASE_URL}/api/shop/my-shops`, { headers });
      const shops = shopsResponse.data?.shops || shopsResponse.data || [];
      
      if (!shops.length) {
        alert('No shop found for your account');
        return;
      }
      
      const shopId = shops[0]._id;
      const businessId = shops[0].businessId?._id || shops[0].businessId;
      
      if (!businessId) {
        alert('No business ID found for your shop');
        return;
      }

      // Fetch packages
      const packagesResponse = await axios.get(
        `${API_BASE_URL}/api/packages/shop/${shopId}?activeOnly=${activeOnly}`,
        { headers }
      );
      setPackages(packagesResponse.data.packages || []);

      // Fetch ONLY business service templates (not professional services)
      // Packages should only use templates controlled by business for pricing consistency
      // Professionals create from templates to participate in packages
      const servicesResponse = await axios.get(
        `${API_BASE_URL}/api/services/templates/business/${businessId}?activeOnly=true`,
        { headers }
      );
      
      // The endpoint returns array directly, not wrapped
      const allServices = Array.isArray(servicesResponse.data) 
        ? servicesResponse.data 
        : (servicesResponse.data.services || []);
      setServices(allServices);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('ogestyle_token');
        window.location.href = '/login';
      }
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, getAuthHeaders, activeOnly]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── PHASE 17: the shop's own sales ────────────────────────────────────────────────────────────────
  const fetchSold = useCallback(
    async (status) => {
      const headers = getAuthHeaders();
      if (!headers) return;

      setSold((prev) => ({ ...prev, loading: true, error: '' }));
      try {
        const params = status && status !== 'all' ? `?status=${encodeURIComponent(status)}` : '';
        const response = await axios.get(`${API_BASE_URL}/api/packages/sold${params}`, { headers });
        setSold({
          purchases: response.data?.purchases || [],
          counts: response.data?.counts || null,
          truncated: response.data?.truncated === true,
          loading: false,
          error: '',
        });
      } catch (error) {
        if (error.response?.status === 401) {
          localStorage.removeItem('ogestyle_token');
          window.location.href = '/login';
          return;
        }
        // A 403 here is the server doing its job: this account may not see the shop's sales.
        setSold((prev) => ({
          ...prev,
          loading: false,
          error:
            error.response?.status === 403
              ? 'Only the business owner can see sold packages.'
              : error.response?.data?.message || 'Could not load sold packages.',
        }));
      }
    },
    [API_BASE_URL, getAuthHeaders],
  );

  const fetchSoldDetail = useCallback(
    async (purchaseId) => {
      const headers = getAuthHeaders();
      if (!headers) return;

      setSoldDetailLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/api/packages/sold/${purchaseId}`, {
          headers,
        });
        setSoldDetail(response.data?.purchase || null);
      } catch (error) {
        setSold((prev) => ({
          ...prev,
          error: error.response?.data?.message || 'Could not load that sale.',
        }));
      } finally {
        setSoldDetailLoading(false);
      }
    },
    [API_BASE_URL, getAuthHeaders],
  );

  // Only fetched when the tab is actually opened, so an owner who never looks at sales pays nothing.
  useEffect(() => {
    if (tab === 'sold' && isR1) fetchSold(soldStatus);
  }, [tab, isR1, soldStatus, fetchSold]);

  // Memoize stringified services for dependency checking
  const servicesJSON = useMemo(() => JSON.stringify(formData.services), [formData.services]);

  // Auto-calculate pricing from services
  useEffect(() => {
    if (!useAutoPrice) {
      setServicesMissingPrices([]);
      return;
    }
    
    if (!formData.services.length) {
      setServicesMissingPrices([]);
      return;
    }
    
    if (!services.length) {
      setServicesMissingPrices([]);
      return;
    }

    let regularTotal = 0;
    let hasValidServices = false;
    const missingPrices = [];
    formData.services.forEach((item) => {
      if (!item.serviceId) return;
      const service = services.find(s => s._id === item.serviceId);
      if (!service) return;

      // Phase 4B: price each line through the SHARED engine, not as `service.price × quantity`.
      //
      // The flat assumption is wrong for every type the engine knows: a per-unit service prices from
      // `pricePerUnit`, an hourly one from `hourlyRate`, and a tiered one from the option chosen — not
      // from the stored `price`. The server now computes the authoritative `regularPrice` the same way
      // through `utils/packagePricing.js`, so this mirror exists purely so the number on screen matches
      // what the server will store.
      const resolved = resolveServicePricing(service, {
        tierId: item.tierId || undefined,
      });

      if (resolved?.requiresSelection) {
        // A tiered service with no option chosen yet. Refusing beats guessing: the server will not
        // accept a package it cannot price, and an invented figure would only fail on save.
        missingPrices.push(`${service.name} (choose an option)`);
        return;
      }

      const unitPrice = Number(resolved?.unitPrice);
      if (!Number.isFinite(unitPrice) || unitPrice <= 0) {
        missingPrices.push(service.name);
        return;
      }

      // The SAME two-dimension rule the server prices with: units per session × sessions. Using only one
      // of them is what priced 10 fingers × 3 sessions as three fingers, and a 2-hour session as one hour.
      // Still deliberately NOT capped by the per-booking maximum — that caps ONE booking, not what a
      // package contains.
      //
      // `item.` — the value the operator typed, which lives on the FORM ROW. Reading it from the catalog
      // `service` instead returns undefined, silently falls back to 1 below, and leaves the calculation at
      // `unitPrice × sessions`: $30 × 3 = $90 for what should have been $30 × 5 × 3 = $450. Two different
      // things are named `service` in this file (the catalog entry here, the form row in the JSX), which is
      // exactly how that slipped through.
      const unitsPerSession = Math.max(1, parseInt(item.unitsPerSession, 10) || 1);
      const sessions = Math.max(1, parseInt(item.quantity, 10) || 1);
      // A BUNDLE REPEATS BY VISITS, NOT BY QUANTITY (2026-10-09). Every service in a bundle is quantity 1 and
      // the number of times the whole set is delivered is `totalSessions`, so multiplying by `quantity` priced a
      // four-visit bundle as ONE visit. The server's engine does the same thing, which is why this mirror must
      // match it — a screen and a stored price that disagree is worse than either being wrong on its own.
      const multiplier = isBundleForm
        ? Math.max(1, parseInt(formData.totalSessions, 10) || 1)
        : sessions;
      regularTotal += unitPrice * unitsPerSession * multiplier;
      hasValidServices = true;
    });

    setServicesMissingPrices(missingPrices);
    if (hasValidServices && regularTotal > 0) {
      const packagePrice = regularTotal * (1 - discountPercent / 100);
      setFormData(prev => ({
        ...prev,
        regularPrice: regularTotal.toFixed(2),
        packagePrice: packagePrice.toFixed(2)
      }));
    } else {
      // Don't clear prices if user manually entered them
      if (missingPrices.length > 0 && !formData.regularPrice) {
        // Auto-disable smart pricing if services are missing prices
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useAutoPrice, discountPercent, servicesJSON, services, formData.totalSessions, isBundleForm]);

  // Fetch pricing suggestions from previous packages and restore discount when editing
  useEffect(() => {
    if (!showModal || packages.length === 0) return;

    // Calculate discount from existing package when editing
    if (editingPackage && editingPackage.regularPrice > 0 && editingPackage.packagePrice > 0 && !initialDiscountSet) {
      const existingDiscount = Math.round(((editingPackage.regularPrice - editingPackage.packagePrice) / editingPackage.regularPrice) * 100);
      if (existingDiscount > 0 && existingDiscount <= 50) {
        setDiscountPercent(existingDiscount);
        setInitialDiscountSet(true);
      }
    }

    const avgDiscount = packages.reduce((acc, pkg) => {
      if (pkg.regularPrice > 0) {
        const discount = ((pkg.regularPrice - pkg.packagePrice) / pkg.regularPrice) * 100;
        return acc + discount;
      }
      return acc;
    }, 0) / packages.length;

    const mostCommonValidity = packages.reduce((acc, pkg) => {
      const days = pkg.validityPeriod?.value || 90;
      acc[days] = (acc[days] || 0) + 1;
      return acc;
    }, {});

    const popularValidity = Object.keys(mostCommonValidity).reduce((a, b) => 
      mostCommonValidity[a] > mostCommonValidity[b] ? a : b, 90
    );

    setPricingSuggestions({
      avgDiscount: Math.round(avgDiscount),
      popularValidity: parseInt(popularValidity)
    });
  }, [showModal, packages, editingPackage, initialDiscountSet]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const headers = getAuthHeaders();
    if (!headers) return;

    // Validation
    if (!formData.regularPrice || !formData.packagePrice) {
      if (servicesMissingPrices.length > 0) {
        setSaveError(`Cannot calculate prices - ${servicesMissingPrices.length} service(s) missing prices: ${servicesMissingPrices.join(', ')}. Please set prices in Services Management or disable Smart Pricing Calculator.`);
      } else {
        setSaveError('Regular price and package price are required. Make sure services are selected and have prices.');
      }
      return;
    }

    const totalServiceQty = calculateTotalServiceQuantity();
    const totalSessions = parseInt(formData.totalSessions);

    // ── THE MODEL IS CHECKED FIRST (§3.4 / §7.7) ───────────────────────────────────────────────
    // Before the price, because these errors are about what the package IS — and the server checks the
    // model before the pricing engine for the same reason. The codes are the SERVER's codes wherever the
    // server has one, so the shop sees the same refusal here as it would from the API.
    const composition = validateCompositionChoice({
      typeId: formData.packageType,
      lines: formData.services,
      totalSessions,
      coveredPeople: formData.coveredPeople,
    });
    if (!composition.ok) {
      setSaveError(composition.message);
      return;
    }

    // ── THE QUOTA RULE APPLIES ONLY TO A QUOTA (§3.5.1) ────────────────────────────────────────
    // A bundle's visits are not the sum of its services, so asking them to balance is what made every
    // valid bundle look invalid. `typeNeedsQuantities` is the one place that decides.
    if (typeNeedsQuantities(formData.packageType)) {
      if (totalServiceQty > totalSessions) {
        setSaveError(`Service quantities (${totalServiceQty}) cannot exceed total sessions (${totalSessions}). Please adjust quantities or increase total sessions.`);
        return;
      }

      // Warn if service quantities are less than total sessions (unused sessions)
      if (totalServiceQty < totalSessions) {
        setSaveError(`You have ${totalSessions - totalServiceQty} unused sessions. Service quantities (${totalServiceQty}) should equal total sessions (${totalSessions}). Please adjust quantities or reduce total sessions.`);
        return;
      }

      if (totalServiceQty === 0) {
        setSaveError('Please add at least one service with quantity > 0.');
        return;
      }
    }

    if (parseFloat(formData.packagePrice) >= parseFloat(formData.regularPrice)) {
      setSaveError('Package price must be less than regular price!');
      return;
    }

    const discountPct = ((parseFloat(formData.regularPrice) - parseFloat(formData.packagePrice)) / parseFloat(formData.regularPrice)) * 100;
    if (discountPct < 5) {
      setSaveError('Discount must be at least 5% to create a package.');
      return;
    }

    setSaveLoading(true);
    setSaveError('');

    try {
      // PHASE 4 FIX: Use my-shops endpoint instead of User.shopIds
      const shopsResponse = await axios.get(`${API_BASE_URL}/api/shop/my-shops`, { headers });
      const shops = shopsResponse.data?.shops || shopsResponse.data || [];
      const shopId = shops[0]?._id;

      const payload = {
        ...formData,
        shopId,
        regularPrice: parseFloat(formData.regularPrice),
        packagePrice: parseFloat(formData.packagePrice),
        totalSessions: parseInt(formData.totalSessions),
        validityPeriod: { value: parseInt(formData.validityDays), unit: 'days' },
        // The composition model, DERIVED from the type the shop chose rather than read back off the form —
        // including `type` (the stored enum) and the services, whose quantities are forced to 1 for a bundle
        // because a bundle's visit is the unit and a quantity would mean nothing.
        ...(compositionPayload({
          typeId: formData.packageType,
          lines: formData.services,
          totalSessions: parseInt(formData.totalSessions) || 0,
          coveredPeople: formData.coveredPeople,
          holderModel: formData.holderModel,
        }) || {}),
      };

      if (editingPackage) {
        await axios.patch(`${API_BASE_URL}/api/packages/${editingPackage._id}`, payload, { headers });
        alert('Package updated successfully!');
      } else {
        await axios.post(`${API_BASE_URL}/api/packages`, payload, { headers });
        alert('Package created successfully!');
      }

      setShowModal(false);
      setEditingPackage(null);
      resetForm();
      fetchData();
    } catch (error) {
      // Handle locked package error
      if (error.response?.status === 403 && error.response?.data?.locked) {
        setSaveError(`🔒 ${error.response.data.message || 'This package cannot be edited because customers have purchased it.'}`);
        return;
      }
      
      const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Failed to save package. Please try again.';
      setSaveError(`Error: ${errorMsg}`);
    } finally {
      setSaveLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      type: 'service_bundle',
      packageType: '',
      holderModel: 'single',
      coveredPeople: '',
      regularPrice: '',
      packagePrice: '',
      totalSessions: '',
      validityDays: 90,
      services: [{ serviceId: '', quantity: 1 }]
    });
    setUseAutoPrice(true);
    setDiscountPercent(20);
    setSaveError('');
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name,
      description: pkg.description || '',
      type: pkg.type,
      // Read BACK from the stored model, so editing an existing package opens on the type it actually IS
      // rather than on the form's default. A saved package whose model cannot be recognised leaves the
      // choice empty and the form asks again, which is honest — guessing a type would show the shop a
      // customer sentence its package does not match.
      packageType: inferTypeId(pkg),
      holderModel: pkg.holderModel || 'single',
      coveredPeople: pkg.coveredPeople ?? '',
      regularPrice: pkg.regularPrice,
      packagePrice: pkg.packagePrice,
      totalSessions: pkg.totalSessions,
      validityDays: pkg.validityPeriod?.value || 90,
      services: pkg.services.map(s => ({ serviceId: s.serviceId._id || s.serviceId, quantity: s.quantity }))
    });
    setUseAutoPrice(false); // Editing existing, don't auto-calculate
    setSaveError('');
    setShowModal(true);
  };

  /**
   * COPY THE LINK A SHOP CAN POST.
   *
   * The destination is the package's own purchase page, which already works for a visitor with no
   * account — so this is the whole feature: a shop copies it into Instagram/Facebook/WhatsApp and a
   * customer lands on the package and can pay.
   *
   * The clipboard API needs a secure context. It is available on https and on localhost, and the
   * fallback covers a plain-http deployment (a shop's staging box) rather than failing silently —
   * a "Copy" button that does nothing is worse than no button.
   */
  const copyPackageLink = async (pkg) => {
    const { origin } = window.location;
    const url = `${origin}/packages/${pkg._id}/purchase`;

    const fallbackCopy = () => {
      const area = document.createElement("textarea");
      area.value = url;
      area.setAttribute("readonly", "");
      area.style.position = "fixed";
      area.style.opacity = "0";
      document.body.appendChild(area);
      area.select();
      try {
        document.execCommand("copy");
      } catch {
        // Nothing left to try; the link is in the field the user just selected.
      }
      document.body.removeChild(area);
    };

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        fallbackCopy();
      }
      setCopiedPackageId(pkg._id);
      setTimeout(() => setCopiedPackageId(null), 2500);
    } catch {
      fallbackCopy();
      setCopiedPackageId(pkg._id);
      setTimeout(() => setCopiedPackageId(null), 2500);
    }
  };

  const handleDelete = async (packageId) => {
    if (!window.confirm('Are you sure you want to deactivate this package?')) return;

    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      await axios.delete(`${API_BASE_URL}/api/packages/${packageId}`, { headers });
      alert('Package deactivated successfully');
      fetchData();
    } catch (error) {
      alert('Failed to deactivate package');
    }
  };

  const addServiceRow = () => {
    setFormData({
      ...formData,
      services: [...formData.services, { serviceId: '', quantity: 1 }]
    });
  };

  const removeServiceRow = (index) => {
    setFormData({
      ...formData,
      services: formData.services.filter((_, i) => i !== index)
    });
  };

  const updateServiceRow = (index, field, value) => {
    const updated = [...formData.services];
    // Replace the row rather than mutating it, so the state update is honest about what changed.
    updated[index] = { ...updated[index], [field]: value };

    // Switching the service invalidates any option or unit count chosen for the previous one. A leftover
    // tier id cannot be resolved against the new service's options, and — worse — a leftover
    // `unitsPerSession` would keep multiplying the price even for a type whose row no longer shows that
    // input, so a fixed service would silently be priced at ×10. Clear both so the operator is asked again.
    if (field === 'serviceId') {
      delete updated[index].tierId;
      delete updated[index].unitsPerSession;
    }

    setFormData({ ...formData, services: updated });
    
    // Clear error if quantity issue was fixed
    if (field === 'quantity' && saveError.includes('Service quantities')) {
      setSaveError('');
    }
  };

  // What ONE of this service costs, quoted through the SAME engine the server prices packages with
  // (Phase 4B). The dropdown used to print `service.price`, which is only a legacy alias: for a per-unit
  // service it is NOT the per-unit price, and for an hourly one it is NOT the rate. So the menu quoted a
  // figure the package would never use — Nail Art showed $15 while the package priced it at $10 a nail.
  const describeUnitPrice = useCallback((service) => {
    const resolved = resolveServicePricing(service, {});

    if (resolved?.requiresSelection) {
      const prices = (resolved.selectionOptions || [])
        .map((option) => Number(option.price))
        .filter((price) => Number.isFinite(price) && price > 0);
      return prices.length ? `from $${Math.min(...prices)}` : 'choose an option';
    }

    const unitPrice = Number(resolved?.unitPrice);
    if (!Number.isFinite(unitPrice) || unitPrice <= 0) return '';

    const type = String(service?.pricingType || '').toLowerCase();
    if (type === 'per_unit') {
      return `$${unitPrice} per ${service?.unitLabel || 'unit'}`;
    }
    if (type === 'hourly') return `$${unitPrice} per hour`;
    return `$${unitPrice}`;
  }, []);

  // The noun for ONE unit of this service — "fingers", "hours". Only a per-unit or hourly service has
  // one; every other type's session is simply one service, so there is no unit to name.
  const unitNounFor = useCallback(
    (serviceId) => {
      const selected = services.find((s) => s._id === serviceId);
      const type = String(selected?.pricingType || '').toLowerCase();
      if (type === 'per_unit') return selected?.unitLabel || 'units';
      if (type === 'hourly') return 'hours';
      return '';
    },
    [services],
  );

  if (loading) {
    return (
      <SidebarLayout>
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', marginBottom: '16px' }}>Loading packages...</div>
        </div>
      </SidebarLayout>
    );
  }

  const savings = formData.regularPrice && formData.packagePrice 
    ? ((parseFloat(formData.regularPrice) - parseFloat(formData.packagePrice)) / parseFloat(formData.regularPrice) * 100).toFixed(0)
    : 0;

  return (
    <SidebarLayout>
      <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '32px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div>
            <h1 style={{ fontSize: '32px', fontWeight: '700', color: '#1a1a1a', margin: 0 }}>
              Package Management
            </h1>
            <p style={{ color: '#666', marginTop: '8px' }}>
              {tab === 'sold'
                ? 'Every package your shop has sold, and how much of each is left'
                : 'Create and manage service packages for your shop'}
            </p>
          </div>
          <div style={{ display: tab === 'packages' ? 'flex' : 'none', gap: '12px', alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: '#666' }}>Active only</span>
            </label>
            {/* Owner-only. R2 sees the list below but not this button. */}
            {!isR2 && (
              <button
                onClick={() => { resetForm(); setEditingPackage(null); setShowModal(true); }}
                style={{
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
                }}
                onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
              >
                + Create Package
              </button>
            )}
          </div>
        </div>

        {/* Tabs. "Sold" is an OWNER view and is not rendered for anyone else — and the server refuses
            them regardless, because a hidden tab is not a permission. */}
        <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid #e5e7eb', marginBottom: '24px' }}>
          {[
            { key: 'packages', label: 'Packages' },
            ...(isR1 ? [{ key: 'sold', label: 'Sold' }] : []),
          ].map((entry) => (
            <button
              key={entry.key}
              type="button"
              onClick={() => setTab(entry.key)}
              style={{
                padding: '10px 18px',
                background: 'none',
                border: 'none',
                borderBottom: `3px solid ${tab === entry.key ? '#2d6da3' : 'transparent'}`,
                color: tab === entry.key ? '#2d6da3' : '#6b7280',
                fontWeight: '600',
                fontSize: '15px',
                cursor: 'pointer',
              }}
            >
              {entry.label}
            </button>
          ))}
        </div>

        {tab === 'sold' && isR1 && (
          <div>
            {soldDetail ? (
              <div>
                <button
                  type="button"
                  onClick={() => setSoldDetail(null)}
                  style={{ marginBottom: '16px', padding: '8px 14px', background: 'white', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', color: '#374151' }}
                >
                  ← All sales
                </button>
                <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '24px' }}>
                  <h3 style={{ margin: '0 0 4px 0', fontSize: '20px', color: '#111827' }}>
                    {soldDetail.packageName}
                  </h3>
                  <p style={{ margin: '0 0 20px 0', color: '#6b7280', fontSize: '14px' }}>
                    {soldDetail.sessionsUsed} of {soldDetail.totalSessions} sessions used · {soldDetail.sessionsRemaining} remaining
                  </p>
                  {/* Contact details live HERE and not in the list: the wide view stays scannable and
                      PII stays in the drill-down. */}
                  <dl style={{ display: 'grid', gridTemplateColumns: 'minmax(140px, max-content) 1fr', rowGap: '10px', columnGap: '24px', margin: '0 0 20px 0', fontSize: '14px' }}>
                    <dt style={{ color: '#6b7280' }}>Buyer</dt><dd style={{ margin: 0, color: '#111827' }}>{soldDetail.buyerName || '—'}</dd>
                    <dt style={{ color: '#6b7280' }}>Email</dt><dd style={{ margin: 0, color: '#111827' }}>{soldDetail.buyerEmail || '—'}</dd>
                    <dt style={{ color: '#6b7280' }}>Phone</dt><dd style={{ margin: 0, color: '#111827' }}>{soldDetail.buyerPhone || '—'}</dd>
                    <dt style={{ color: '#6b7280' }}>Purchased</dt><dd style={{ margin: 0, color: '#111827' }}>{soldDetail.purchasedAt ? new Date(soldDetail.purchasedAt).toLocaleDateString() : '—'}</dd>
                    <dt style={{ color: '#6b7280' }}>Paid</dt><dd style={{ margin: 0, color: '#111827' }}>{soldDetail.currency} {Number(soldDetail.pricePaid || 0).toFixed(2)}</dd>
                    <dt style={{ color: '#6b7280' }}>Valid until</dt><dd style={{ margin: 0, color: '#111827' }}>{soldDetail.validUntil ? new Date(soldDetail.validUntil).toLocaleDateString() : '—'}</dd>
                    <dt style={{ color: '#6b7280' }}>Status</dt><dd style={{ margin: 0, color: '#111827' }}>{soldDetail.effectiveStatus}{soldDetail.status && soldDetail.status !== soldDetail.effectiveStatus ? ` (stored as ${soldDetail.status})` : ''}</dd>
                    <dt style={{ color: '#6b7280' }}>Location</dt><dd style={{ margin: 0, color: '#111827' }}>{soldDetail.shopName || '—'}</dd>
                  </dl>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '15px', color: '#374151' }}>Sessions</h4>
                  {soldDetail.sessions?.length ? (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                      <thead>
                        <tr style={{ textAlign: 'left', color: '#6b7280' }}>
                          <th style={{ padding: '8px 10px', borderBottom: '1px solid #e5e7eb' }}>#</th>
                          <th style={{ padding: '8px 10px', borderBottom: '1px solid #e5e7eb' }}>Date</th>
                          <th style={{ padding: '8px 10px', borderBottom: '1px solid #e5e7eb' }}>Time</th>
                          <th style={{ padding: '8px 10px', borderBottom: '1px solid #e5e7eb' }}>Service</th>
                          <th style={{ padding: '8px 10px', borderBottom: '1px solid #e5e7eb' }}>Professional</th>
                          <th style={{ padding: '8px 10px', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {soldDetail.sessions.map((session) => (
                          <tr key={session.bookingId}>
                            <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6' }}>{session.sessionNumber || '—'}</td>
                            <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6' }}>{session.appointmentDate || '—'}</td>
                            <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6' }}>{session.startTime || '—'}</td>
                            <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6' }}>{session.serviceName || '—'}</td>
                            <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6' }}>{session.professionalName || '—'}</td>
                            <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6' }}>{session.status || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p style={{ color: '#6b7280', fontSize: '14px' }}>No sessions booked on this package yet.</p>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
                  {['all', 'active', 'expired', 'fully_used'].map((key) => {
                    const label = key === 'fully_used' ? 'Fully used' : key.charAt(0).toUpperCase() + key.slice(1);
                    const count = key === 'all' ? sold.counts?.all : sold.counts?.[key === 'fully_used' ? 'completed' : key];
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setSoldStatus(key)}
                        style={{ padding: '8px 14px', borderRadius: '999px', border: `1px solid ${soldStatus === key ? '#2d6da3' : '#d1d5db'}`, background: soldStatus === key ? '#eff6ff' : 'white', color: soldStatus === key ? '#2d6da3' : '#374151', fontWeight: '600', fontSize: '13px', cursor: 'pointer' }}
                      >
                        {label}{count === undefined || count === null ? '' : ` (${count})`}
                      </button>
                    );
                  })}
                </div>

                {sold.loading && <p style={{ color: '#6b7280' }}>Loading sales…</p>}
                {sold.error && (
                  <div style={{ padding: '12px 14px', background: '#fee2e2', color: '#991b1b', borderRadius: '8px', fontSize: '14px' }}>{sold.error}</div>
                )}
                {!sold.loading && !sold.error && sold.purchases.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '48px 20px', background: '#f9fafb', borderRadius: '12px', border: '2px dashed #e5e7eb' }}>
                    <div style={{ fontSize: '40px', marginBottom: '12px' }}>🧾</div>
                    <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>No sales yet</h3>
                    <p style={{ color: '#6b7280' }}>When a customer buys one of your packages it will appear here.</p>
                  </div>
                )}

                {!sold.loading && !sold.error && sold.purchases.length > 0 && (
                  <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                      <thead>
                        <tr style={{ textAlign: 'left', color: '#6b7280', background: '#f9fafb' }}>
                          <th style={{ padding: '12px 14px' }}>Package</th>
                          <th style={{ padding: '12px 14px' }}>Buyer</th>
                          <th style={{ padding: '12px 14px' }}>Purchased</th>
                          <th style={{ padding: '12px 14px' }}>Paid</th>
                          <th style={{ padding: '12px 14px' }}>Sessions</th>
                          <th style={{ padding: '12px 14px' }}>Expires</th>
                          <th style={{ padding: '12px 14px' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sold.purchases.map((row) => (
                          <tr
                            key={row.purchaseId}
                            onClick={() => !soldDetailLoading && fetchSoldDetail(row.purchaseId)}
                            style={{ cursor: 'pointer', borderTop: '1px solid #f3f4f6' }}
                          >
                            <td style={{ padding: '12px 14px', color: '#111827', fontWeight: '600' }}>{row.packageName}</td>
                            <td style={{ padding: '12px 14px', color: '#374151' }}>
                              {row.buyerName}
                              <div style={{ color: '#9ca3af', fontSize: '12px' }}>{row.buyerEmail}</div>
                            </td>
                            <td style={{ padding: '12px 14px', color: '#374151' }}>{row.purchasedAt ? new Date(row.purchasedAt).toLocaleDateString() : '—'}</td>
                            <td style={{ padding: '12px 14px', color: '#374151' }}>{row.currency} {Number(row.pricePaid || 0).toFixed(2)}</td>
                            <td style={{ padding: '12px 14px', color: '#374151' }}>{row.sessionsUsed} / {row.totalSessions}</td>
                            <td style={{ padding: '12px 14px', color: '#374151' }}>{row.validUntil ? new Date(row.validUntil).toLocaleDateString() : '—'}</td>
                            <td style={{ padding: '12px 14px' }}>
                              <span style={{ padding: '4px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: '600', background: row.effectiveStatus === 'active' ? '#dcfce7' : row.effectiveStatus === 'completed' ? '#e0e7ff' : '#f3f4f6', color: row.effectiveStatus === 'active' ? '#166534' : row.effectiveStatus === 'completed' ? '#3730a3' : '#4b5563' }}>
                                {row.effectiveStatus}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {sold.truncated && (
                      <p style={{ margin: 0, padding: '12px 14px', color: '#92400e', background: '#fffbeb', fontSize: '13px' }}>
                        Showing the most recent sales only.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Packages Grid */}
        {packages.length === 0 ? (
          <div style={{
            display: tab === 'packages' ? 'block' : 'none',
            textAlign: 'center',
            padding: '60px 20px',
            background: '#f9fafb',
            borderRadius: '12px',
            border: '2px dashed #e5e7eb'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
            <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
              No packages yet
            </h3>
            <p style={{ color: '#6b7280', marginBottom: '24px' }}>
              Create your first package to offer bundled services at discounted prices
            </p>
            <button
              onClick={() => { resetForm(); setShowModal(true); }}
              style={{
                padding: '12px 24px',
                background: '#2d6da3',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600'
              }}
            >
              Create First Package
            </button>
          </div>
        ) : (
          <div style={{ 
            display: tab === 'packages' ? 'grid' : 'none',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
            gap: '24px',
            fontFamily: 'Inter, sans-serif'
          }}>
            {packages.map((pkg) => {
              const discount = pkg.regularPrice > 0 
                ? Math.round(((pkg.regularPrice - pkg.packagePrice) / pkg.regularPrice) * 100) 
                : 0;
              
              return (
                <div
                  key={pkg._id}
                  style={{
                    background: 'white',
                    borderRadius: '12px',
                    padding: '24px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    border: '1px solid #e5e7eb',
                    position: 'relative',
                    transition: 'transform 0.2s, box-shadow 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                  }}
                >
                  {discount > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '16px',
                      right: '16px',
                      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                      color: 'white',
                      padding: '6px 12px',
                      borderRadius: '20px',
                      fontSize: '14px',
                      fontWeight: '700'
                    }}>
                      {discount}% OFF
                    </div>
                  )}

                  <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1a1a1a', marginBottom: '8px' }}>
                    {pkg.name}
                  </h3>
                  
                  {pkg.description && (
                    <p style={{ color: '#666', fontSize: '14px', marginBottom: '16px', lineHeight: '1.5' }}>
                      {pkg.description}
                    </p>
                  )}

                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '32px', fontWeight: '700', color: '#2d6da3' }}>
                      ${pkg.packagePrice}
                    </div>
                    {pkg.regularPrice > pkg.packagePrice && (
                      <div style={{ fontSize: '16px', color: '#999', textDecoration: 'line-through' }}>
                        ${pkg.regularPrice}
                      </div>
                    )}
                  </div>

                  <div style={{ 
                    borderTop: '1px solid #e5e7eb', 
                    paddingTop: '16px', 
                    marginBottom: '16px',
                    fontSize: '14px',
                    color: '#666'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span>📦 Services:</span>
                      <span style={{ fontWeight: '600', color: '#1a1a1a' }}>{pkg.services?.length || 0}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span>🎫 Total Sessions:</span>
                      <span style={{ fontWeight: '600', color: '#1a1a1a' }}>{pkg.totalSessions}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span>⏰ Validity:</span>
                      <span style={{ fontWeight: '600', color: '#1a1a1a' }}>
                        {pkg.validityPeriod?.value} {pkg.validityPeriod?.unit}
                      </span>
                    </div>
                    {pkg.createdAt && (
                      <div style={{ 
                        fontSize: '12px', 
                        color: '#6b7280',
                        marginBottom: '8px',
                        fontFamily: 'Inter, sans-serif'
                      }}>
                        <span>📅 Created: </span>
                        <span style={{ fontWeight: '500' }}>
                          {new Date(pkg.createdAt).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                          })}
                        </span>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span>👥 Subscribers:</span>
                      <span style={{ 
                        fontWeight: '600', 
                        color: (pkg.subscriberCount > 0) ? '#10b981' : '#ef4444'
                      }}>
                        {pkg.subscriberCount !== undefined ? pkg.subscriberCount : 0}
                        {(pkg.subscriberCount === 0 || pkg.subscriberCount === undefined) && (
                          <span style={{ 
                            fontSize: '11px',
                            marginLeft: '6px',
                            background: '#fee2e2',
                            color: '#dc2626',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontWeight: '600'
                          }}>
                            No pros yet
                          </span>
                        )}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>🛒 Purchases:</span>
                      <span style={{ fontWeight: '600', color: '#1a1a1a' }}>{pkg.currentPurchases || 0}</span>
                    </div>
                  </div>

                  {/* D43 — WHY this package is not reaching customers. Without it the shop sees a
                      package that looks fine and has no idea nothing is being sold from it. The
                      commonest cause by far is the two settings the link points at: packages not
                      switched on, and the agreement not accepted. */}
                  {pkg.sellability && !pkg.sellability.sellable ? (
                    <div
                      style={{
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '8px',
                        padding: '12px',
                        marginBottom: '16px'
                      }}
                    >
                      <div
                        style={{
                          fontSize: '12px',
                          fontWeight: '700',
                          color: '#991b1b',
                          marginBottom: '4px'
                        }}
                      >
                        Not visible to customers
                      </div>
                      {(pkg.sellability.messages || []).map((message, index) => (
                        <div
                          key={index}
                          style={{
                            fontSize: '12px',
                            color: '#7f1d1d',
                            lineHeight: '1.5'
                          }}
                        >
                          • {message}
                        </div>
                      ))}
                      {/* Owner-only. /dashboard/settings/packages is R1-guarded, so offering this to R2 only
                          bounced them to /dashboard. R2 still sees the reason above, which is the point of
                          the warning. */}
                      {!isR2 && (
                        <Link
                          to="/dashboard/settings/packages"
                          style={{
                            display: 'inline-block',
                            marginTop: '8px',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#2563eb',
                            textDecoration: 'underline'
                          }}
                        >
                          Go to package settings to agree and switch packages on →
                        </Link>
                      )}
                    </div>
                  ) : null}

                  {/* Subscriber Avatars - Compact Display */}
                  {pkg.subscriberDetails && pkg.subscriberDetails.length > 0 && (
                    <div 
                      onClick={() => {
                        setSelectedPackageForSubscribers(pkg);
                        setShowSubscribersModal(true);
                      }}
                      style={{
                        background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                        border: '1px solid #10b981',
                        borderRadius: '8px',
                        padding: '12px',
                        marginBottom: '16px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.02)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div style={{ 
                        fontSize: '12px', 
                        fontWeight: '600', 
                        color: '#065f46',
                        marginBottom: '8px'
                      }}>
                        ✅ Subscribed Professionals
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        {/* Show first 4 avatars */}
                        {pkg.subscriberDetails.slice(0, 4).map((subscriber, idx) => (
                          <div 
                            key={idx}
                            style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '50%',
                              background: subscriber.profilePicture 
                                ? `url(${subscriber.profilePicture})` 
                                : 'linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)',
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: '14px',
                              fontWeight: '600',
                              border: '2px solid white',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                          >
                            {!subscriber.profilePicture && subscriber.name?.charAt(0).toUpperCase()}
                          </div>
                        ))}
                        {/* Show +X more if there are more than 4 */}
                        {pkg.subscriberDetails.length > 4 && (
                          <div style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            background: '#10b981',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: '700',
                            border: '2px solid white',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          }}>
                            +{pkg.subscriberDetails.length - 4}
                          </div>
                        )}
                        <div style={{ flex: 1 }}></div>
                        {/* The drill-down is per-professional EARNINGS, which the server now withholds from a
                            non-owner. R2 keeps the subscriber count and the avatars above. */}
                        {isR2 ? null : (
                        <div style={{
                          fontSize: '12px',
                          color: '#059669',
                          fontWeight: '600'
                        }}>
                          Click for details →
                        </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* What the SHOP makes from this package. Shown as a RANGE, and shown ONCE.
                      The shop's share is (net - the professional's pay), and that pay comes from
                      each professional's own commission split — so the figure genuinely varies by
                      who delivers. A single figure would be wrong; a row per professional would be
                      ten rows on a card for ten subscribers. The range collapses to one figure
                      when everyone shares a split. */}
                  {/* OWNER-ONLY. The shop's own margin, and the comparison against what the service earns
                      booked individually. R2 has no business seeing this. The server no longer sends
                      `businessEarnings` to a non-owner at all, so this is the second layer rather than the
                      only one — a hidden panel is not enforcement. */}
                  {!isR2 && pkg.businessEarnings ? (
                    (() => {
                      const range = (min, max) => {
                        const low = Number(min || 0).toFixed(2);
                        const high = Number(max || 0).toFixed(2);
                        return low === high ? `$${low}` : `$${low}–$${high}`;
                      };
                      const e = pkg.businessEarnings;
                      return (
                        <div style={{
                          background: '#f0f9ff',
                          border: '1px solid #bae6fd',
                          borderRadius: '8px',
                          padding: '12px',
                          marginBottom: '16px'
                        }}>
                          <div style={{ fontSize: '12px', fontWeight: '600', color: '#0c4a6e', marginBottom: '6px' }}>
                            💰 What you make on this package
                          </div>
                          <div style={{ fontSize: '18px', fontWeight: '700', color: '#0369a1' }}>
                            {range(e.packagePerSessionMin, e.packagePerSessionMax)} average per session
                          </div>
                          <div style={{ fontSize: '12px', color: '#075985', marginTop: '2px' }}>
                            {range(e.packageTotalMin, e.packageTotalMax)} for this package
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '6px' }}>
                            Booked individually: {range(e.normalPerSessionMin, e.normalPerSessionMax)} average per session
                            {' '}({range(e.normalTotalMin, e.normalTotalMax)} total)
                          </div>
                          {e.basedOnProfessionals > 1 ? (
                            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                              Range across your {e.basedOnProfessionals} professionals — each has their own commission split.
                            </div>
                          ) : null}
                        </div>
                      );
                    })()
                  ) : null}

                  {/* Owner-only CRUD (R2 may view). The server refuses both actions for R2. */}
                  {!isR2 && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleEdit(pkg)}
                        style={{
                          flex: 1,
                          padding: '10px',
                          background: '#2d6da3',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '600'
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(pkg._id)}
                        style={{
                          flex: 1,
                          padding: '10px',
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '600'
                        }}
                      >
                        Deactivate
                      </button>
                    </div>
                  )}

                  {/*
                    SHARE LINK — for everyone who can see this page, R1 and R2 alike. It changes
                    nothing, and it is the shop's own marketing: paste it into a social post and a
                    customer lands on this package's purchase page, which works without an account.
                  */}
                  {pkg.isActive && (
                    <div style={{ marginTop: '8px' }}>
                      <button
                        type="button"
                        onClick={() => copyPackageLink(pkg)}
                        style={{
                          width: '100%',
                          padding: '10px',
                          background: copiedPackageId === pkg._id ? '#dcfce7' : '#f1f5f9',
                          color: copiedPackageId === pkg._id ? '#15803d' : '#334155',
                          border: '1px solid #cbd5e1',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '600'
                        }}
                      >
                        {copiedPackageId === pkg._id ? '✓ Link copied' : '🔗 Copy purchase link'}
                      </button>
                      <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#64748b' }}>
                        Post it anywhere — the link opens this package, and customers can buy
                        without an account.
                      </p>
                    </div>
                  )}

                  {!pkg.isActive && (
                    <div style={{
                      marginTop: '12px',
                      padding: '8px',
                      background: '#fef2f2',
                      border: '1px solid #fecaca',
                      borderRadius: '6px',
                      color: '#dc2626',
                      fontSize: '14px',
                      textAlign: 'center',
                      fontWeight: '600'
                    }}>
                      Inactive
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Create/Edit Modal. Never rendered for R2 — an extra guard on top of the hidden controls. */}
        {showModal && !isR2 && (
          <>
            <div 
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.6)',
                zIndex: 999,
                backdropFilter: 'blur(4px)'
              }}
              onClick={() => { setShowModal(false); setEditingPackage(null); }}
            />
            <div style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'white',
              borderRadius: '16px',
              padding: window.innerWidth <= 768 ? '20px' : '32px',
              width: '90%',
              maxWidth: '700px',
              maxHeight: '90vh',
              overflowY: 'auto',
              zIndex: 1000,
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              boxSizing: 'border-box',
              fontFamily: 'Inter, sans-serif'
            }}>
              <h2 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px', color: '#1a1a1a' }}>
                {editingPackage ? 'Edit Package' : 'Create New Package'}
              </h2>
              <p style={{ color: '#666', marginBottom: '24px' }}>
                Bundle services together and offer them at a discounted price
              </p>

              {/* THE VALUE LOCK, SAID BEFORE THE SHOP TYPES (user, 2026-10-09).
                  A package behaves like a printed gift card: once a professional has subscribed they have been
                  quoted a figure and accepted the terms, so the value cannot move. The server ENFORCES this on
                  save; telling them here is so they are not typing into a form that will refuse them at the end.
                  Read from the stamp, never from the live subscriber list — unsubscribing does not unlock it. */}
              {isValueLocked(editingPackage) && (
                <div
                  role="status"
                  style={{
                    marginBottom: '20px',
                    padding: '12px 14px',
                    background: '#fffbeb',
                    border: '1px solid #fde68a',
                    borderRadius: '10px',
                    fontSize: '13px',
                    color: '#92400e',
                    lineHeight: 1.6,
                    fontFamily: 'Inter, sans-serif'
                  }}
                >
                  <strong>🔒 Locked:</strong> a professional has subscribed to this package, so what it is worth
                  cannot change — not the price, the services, the visits, the validity or the shape. The name and
                  the description can still be edited, and you can still take it off sale. To change the value,
                  create a new package.
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* THE TYPE FIRST (§3.4, item 7). It decides which questions follow, shows the customer's own
                    sentence as the shop answers, and states what a bundle session is — so the wording and the
                    refusal rules live in one tested module rather than in this file's JSX. */}
                {/* THE TYPE STEP DECIDES THE MODEL, AND THE MODEL DECIDES WHAT THE PACKAGE IS WORTH, so it
                    locks with the price. It is a component, so it cannot simply take `disabled` — the whole
                    block is made inert and dimmed instead. */}
                <div
                  style={
                    isValueLocked(editingPackage)
                      ? { opacity: 0.6, pointerEvents: 'none' }
                      : undefined
                  }
                  aria-disabled={isValueLocked(editingPackage) || undefined}
                >
                <PackageTypeStep
                  typeId={formData.packageType}
                  holderModel={formData.holderModel}
                  totalSessions={formData.totalSessions}
                  coveredPeople={formData.coveredPeople}
                  lines={formData.services}
                  catalog={services}
                  onChange={(fields) => {
                    setSaveError('');
                    setFormData((prev) => ({ ...prev, ...fields }));
                  }}
                />
                </div>

                {/* Basic Info */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                    Package Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="e.g., 5 Haircut Bundle"
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb',
                      fontSize: '15px',
                      boxSizing: 'border-box',
                      fontFamily: 'Inter, sans-serif'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe what's included in this package..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb',
                      fontSize: '15px',
                      fontFamily: 'Inter, sans-serif',
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* Pricing */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                      Regular Price * ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.regularPrice}
                      onChange={(e) => setFormData({ ...formData, regularPrice: e.target.value })}
                      required
                      placeholder="400.00"
                      disabled={useAutoPrice}
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '8px',
                        border: useAutoPrice && formData.regularPrice ? '2px solid #d1fae5' : '2px solid #e5e7eb',
                        fontSize: '15px',
                        boxSizing: 'border-box',
                        fontFamily: 'Inter, sans-serif',
                        background: useAutoPrice ? '#f9fafb' : 'white',
                        cursor: useAutoPrice ? 'not-allowed' : 'text'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                      Package Price * ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.packagePrice}
                      onChange={(e) => setFormData({ ...formData, packagePrice: e.target.value })}
                      required
                      placeholder="300.00"
                      disabled={useAutoPrice || isValueLocked(editingPackage)}
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '8px',
                        border: useAutoPrice && formData.packagePrice ? '2px solid #d1fae5' : '2px solid #e5e7eb',
                        fontSize: '15px',
                        boxSizing: 'border-box',
                        fontFamily: 'Inter, sans-serif',
                        background: useAutoPrice ? '#f9fafb' : 'white',
                        cursor: useAutoPrice ? 'not-allowed' : 'text'
                      }}
                    />
                  </div>
                </div>

                {/* Auto-Pricing Toggle */}
                {servicesMissingPrices.length > 0 && (
                  <div style={{
                    marginBottom: '20px',
                    padding: '16px',
                    background: '#fef2f2',
                    borderRadius: '8px',
                    border: '2px solid #fecaca'
                  }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#b91c1c',
                      marginBottom: '8px',
                      fontFamily: 'Inter, sans-serif'
                    }}>
                      ⚠️ Services Missing Prices
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: '#991b1b',
                      marginBottom: '10px',
                      fontFamily: 'Inter, sans-serif'
                    }}>
                      The following services don't have prices set:
                      <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                        {servicesMissingPrices.map((name, idx) => (
                          <li key={idx}>{name}</li>
                        ))}
                      </ul>
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#7f1d1d',
                      fontFamily: 'Inter, sans-serif',
                      padding: '10px',
                      background: '#fee2e2',
                      borderRadius: '6px'
                    }}>
                      <strong>To fix:</strong>
                      <ol style={{ margin: '6px 0', paddingLeft: '20px' }}>
                        <li>Go to Services Management page</li>
                        <li>Edit each service and add a price</li>
                        <li>Return here to auto-calculate package pricing</li>
                      </ol>
                      <div style={{ marginTop: '8px', fontStyle: 'italic' }}>
                        Or uncheck "Smart Pricing Calculator" below to enter prices manually.
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ 
                  marginBottom: '20px',
                  padding: '12px',
                  background: '#f0f9ff',
                  borderRadius: '8px',
                  border: '1px solid #bae6fd'
                }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    cursor: 'pointer',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '14px'
                  }}>
                    <input
                      type="checkbox"
                      checked={useAutoPrice}
                      onChange={(e) => setUseAutoPrice(e.target.checked)}
                      disabled={isValueLocked(editingPackage)}
                      style={{ width: '18px', height: '18px', cursor: isValueLocked(editingPackage) ? 'not-allowed' : 'pointer' }}
                    />
                    <span style={{ fontWeight: '600', color: '#0369a1' }}>🧮 Smart Pricing Calculator</span>
                    {useAutoPrice && formData.regularPrice && (
                      <span style={{
                        marginLeft: 'auto',
                        fontSize: '12px',
                        color: '#0891b2',
                        fontWeight: '500'
                      }}>
                        ✨ Live updating
                      </span>
                    )}
                  </label>
                  <p style={{ 
                    margin: '8px 0 0 26px', 
                    fontSize: '13px', 
                    color: '#0c4a6e',
                    fontFamily: 'Inter, sans-serif'
                  }}>
                    Auto-calculate prices from services and apply discount percentage
                  </p>
                </div>

                {/* Discount Slider (only when auto-pricing enabled) */}
                {useAutoPrice && (
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontWeight: '600', 
                      marginBottom: '12px', 
                      color: '#374151',
                      fontFamily: 'Inter, sans-serif'
                    }}>
                      <span>Discount Percentage</span>
                      <span style={{
                        background: discountPercent > 35 ? '#fee2e2' : discountPercent > 20 ? '#fef3c7' : '#d1fae5',
                        color: discountPercent > 35 ? '#b91c1c' : discountPercent > 20 ? '#92400e' : '#065f46',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '14px',
                        fontWeight: '700'
                      }}>
                        {discountPercent}% OFF
                      </span>
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="50"
                      value={discountPercent}
                      onChange={(e) => setDiscountPercent(parseInt(e.target.value))}
                      disabled={isValueLocked(editingPackage)}
                      style={{
                        width: '100%',
                        height: '8px',
                        borderRadius: '4px',
                        outline: 'none',
                        cursor: 'pointer'
                      }}
                    />
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      marginTop: '8px',
                      fontSize: '12px',
                      color: '#6b7280',
                      fontFamily: 'Inter, sans-serif'
                    }}>
                      <span>10% (Conservative)</span>
                      <span>50% (Aggressive)</span>
                    </div>
                    {discountPercent > 40 && (
                      <div style={{
                        marginTop: '12px',
                        padding: '10px',
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '6px',
                        fontSize: '13px',
                        color: '#b91c1c',
                        fontFamily: 'Inter, sans-serif'
                      }}>
                        ⚠️ High discount alert! Make sure this pricing is sustainable for your business.
                      </div>
                    )}
                  </div>
                )}

                {/* Pricing Suggestions */}
                {pricingSuggestions && !editingPackage && (
                  <div style={{
                    marginBottom: '20px',
                    padding: '16px',
                    background: '#fefce8',
                    border: '1px solid #fde047',
                    borderRadius: '8px'
                  }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#854d0e',
                      marginBottom: '10px',
                      fontFamily: 'Inter, sans-serif'
                    }}>
                      💡 Pricing Insights
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: '#713f12',
                      lineHeight: '1.6',
                      fontFamily: 'Inter, sans-serif'
                    }}>
                      <div style={{ marginBottom: '6px' }}>
                        • Your average package discount: <strong>{pricingSuggestions.avgDiscount}%</strong>
                      </div>
                      <div style={{ marginBottom: '6px' }}>
                        • Most used validity period: <strong>{pricingSuggestions.popularValidity} days</strong>
                      </div>
                      <div>
                        • Industry standard: <strong>20-25% off for 3+ services</strong>
                      </div>
                    </div>
                  </div>
                )}

                {savings > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{
                      padding: '12px 16px',
                      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                      color: 'white',
                      borderRadius: '8px',
                      fontWeight: '600',
                      textAlign: 'center',
                      fontSize: '16px'
                    }}>
                      💰 Customer saves {savings}%!
                    </div>
                    {/* THE CAVEAT BELONGS TO THIS FIGURE, so it now sits under it.

                        It was inside the Smart Pricing checkbox's `<label>`, which made it a click target
                        for that checkbox — the shop could not read a sentence without changing a setting.
                        And it is a qualification of the SAVING, not of the calculator: the saving is measured
                        against the shop's service prices, while a professional books from their own price for
                        the same service, so their customers may save less than this badge claims.

                        INFO ONLY — changes no behaviour. */}
                    <div style={{
                      marginTop: '8px',
                      padding: '10px 14px',
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '12.5px',
                      color: '#475569',
                      lineHeight: 1.55,
                      fontFamily: 'Inter, sans-serif'
                    }}>
                      <strong style={{ color: '#334155' }}>Measured against your shop's prices.</strong>{' '}
                      A professional can set their own price for the same service, so their customers may save
                      less than this. Lower the package price if you want the saving to hold for everyone.
                    </div>
                  </div>
                )}

                {/* Service Quantity Summary — QUOTA ONLY (§3.5.1).

                    A bundle's visits are not the sum of its services, so "3 / 5 sessions" would be a
                    meaningless pairing that reads like an error. The block is a check on the quota rule, so it
                    appears only where the quota rule applies. */}
                {formData.services.length > 0 && typeNeedsQuantities(formData.packageType) && (
                  <div style={{
                    marginBottom: '20px',
                    padding: '12px',
                    background: calculateTotalServiceQuantity() > parseInt(formData.totalSessions || 0) ? '#fef2f2' : '#f0fdf4',
                    border: `1px solid ${calculateTotalServiceQuantity() > parseInt(formData.totalSessions || 0) ? '#fecaca' : '#bbf7d0'}`,
                    borderRadius: '8px',
                    fontFamily: 'Inter, sans-serif'
                  }}>
                    <div style={{ 
                      fontSize: '13px',
                      color: calculateTotalServiceQuantity() > parseInt(formData.totalSessions || 0) ? '#b91c1c' : '#15803d',
                      fontWeight: '600'
                    }}>
                      Service Quantities: {calculateTotalServiceQuantity()} / {formData.totalSessions || 0} sessions
                    </div>
                    {calculateTotalServiceQuantity() > parseInt(formData.totalSessions || 0) && (
                      <div style={{ fontSize: '12px', color: '#b91c1c', marginTop: '4px' }}>
                        ⚠️ Total service quantities exceed total sessions!
                      </div>
                    )}
                  </div>
                )}

                {/* Sessions & Validity */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                      {chosenType ? chosenType.sessionsQuestion.replace('?', '') : 'Total Sessions'} *
                    </label>
                    <input
                      type="number"
                      value={formData.totalSessions}
                      disabled={isValueLocked(editingPackage)}
                      onChange={(e) => {
                        setFormData({ ...formData, totalSessions: e.target.value });
                        // Clear error if total sessions was increased to fix quantity issue
                        if (saveError.includes('Service quantities')) {
                          setSaveError('');
                        }
                      }}
                      required
                      placeholder="5"
                      min="1"
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '8px',
                        border: '2px solid #e5e7eb',
                        fontSize: '15px',
                        boxSizing: 'border-box',
                        fontFamily: 'Inter, sans-serif'
                      }}
                    />
                    {/* THE HINT SITS UNDER THE INPUT, not between the label and it.

                        It used to sit above, which pushed this field's input below the one beside it —
                        "How many visits" and "Valid For (Days)" are a two-column row, so a hint line in
                        one column only lands as two inputs at different heights. Underneath, both inputs
                        start at the same place and the hint still reads as belonging to this field. */}
                    {chosenType && (
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px', lineHeight: 1.5 }}>
                        {chosenType.sessionsHint}
                      </div>
                    )}
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                      Valid For (Days)
                    </label>
                    <input
                      type="number"
                      value={formData.validityDays}
                      onChange={(e) => setFormData({ ...formData, validityDays: e.target.value })}
                      placeholder="90"
                      min="1"
                      disabled={isValueLocked(editingPackage)}
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '8px',
                        border: '2px solid #e5e7eb',
                        fontSize: '15px',
                        boxSizing: 'border-box',
                        fontFamily: 'Inter, sans-serif'
                      }}
                    />
                  </div>
                </div>

                {/* Services */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>
                    {isBundleForm
                      ? 'Services done together in one visit *'
                      : oneServiceOnly
                        ? 'The service this package covers *'
                        : 'Included Services *'}
                  </label>
                  {isBundleForm && (
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '10px' }}>
                      Every one of these is done in the same visit, and that visit is one session. A customer may
                      take only some of them and it still uses the session.
                    </div>
                  )}
                  {formData.services.map((service, index) => (
                    <div key={index} style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                      <select
                        value={service.serviceId}
                        onChange={(e) => updateServiceRow(index, 'serviceId', e.target.value)}
                        required
                        disabled={isValueLocked(editingPackage)}
                        style={{
                          flex: 1,
                          padding: '12px',
                          borderRadius: '8px',
                          border: '2px solid #e5e7eb',
                          fontSize: '15px'
                        }}
                      >
                        <option value="">Select service...</option>
                        {services.map(s => (
                          <option key={s._id} value={s._id}>
                            {s.name}
                            {s.isTemplate ? ' (Template)' : ''}
                            {describeUnitPrice(s) ? ` — ${describeUnitPrice(s)}` : ''}
                            {s.staffId?.name ? ` - ${s.staffId.name}` : ''}
                          </option>
                        ))}
                      </select>
                      {/* A tiered (`range`) service has several priced options, so the package has to say
                          WHICH one it includes — the server refuses to price a package without it (Phase
                          4B). Rendered only for range services with priced options. */}
                      {(() => {
                        const selectedService = services.find(s => s._id === service.serviceId);
                        const tiers = Array.isArray(selectedService?.priceTiers) ? selectedService.priceTiers : [];
                        if (String(selectedService?.pricingType || '').toLowerCase() !== 'range' || tiers.length === 0) {
                          return null;
                        }
                        return (
                          <select
                            value={service.tierId || ''}
                            onChange={(e) => updateServiceRow(index, 'tierId', e.target.value)}
                            required
                            disabled={isValueLocked(editingPackage)}
                            style={{
                              width: '200px',
                              padding: '12px',
                              borderRadius: '8px',
                              border: `2px solid ${service.tierId ? '#e5e7eb' : '#fca5a5'}`,
                              fontSize: '15px'
                            }}
                          >
                            <option value="">Choose option...</option>
                            {tiers.map((tier, tierIndex) => (
                              // Must match the engine's tier id resolution: `_id`, then `id`, then index.
                              <option
                                key={tier._id || tier.id || tierIndex}
                                value={String(tier._id || tier.id || tierIndex)}
                              >
                                {tier.label || `Option ${tierIndex + 1}`}{tier.price ? ` - $${tier.price}` : ''}
                              </option>
                            ))}
                          </select>
                        );
                      })()}
                      {/* UNITS (or hours) PER SESSION — only meaningful for per-unit and hourly, where one
                          session is a block of them: 10 fingers, or 2 hours. A fixed or tiered service's
                          session IS one service, so no input is shown and the value stays 1. */}
                      {(() => {
                        const unitNoun = unitNounFor(service.serviceId);
                        if (!unitNoun) return null;
                        return (
                          <>
                            <input
                              type="number"
                              min="1"
                              value={service.unitsPerSession ?? 1}
                              disabled={isValueLocked(editingPackage)}
                              onChange={(e) =>
                                updateServiceRow(index, 'unitsPerSession', e.target.value)
                              }
                              title={`How many ${unitNoun} one session includes`}
                              style={{
                                width: '80px',
                                padding: '12px',
                                borderRadius: '8px',
                                border: '2px solid #e5e7eb',
                                fontSize: '15px'
                              }}
                            />
                            <span
                              style={{
                                alignSelf: 'center',
                                fontSize: '13px',
                                fontWeight: '600',
                                color: '#6b7280',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {unitNoun} per session
                            </span>
                          </>
                        );
                      })()}
                      {/* How many SESSIONS the package includes. This is the number that must add up to
                          Total Sessions below — never the unit count. The label is a visible suffix, not a
                          placeholder: the field always holds a value (it defaults to 1).

                          NOT SHOWN FOR A BUNDLE. A bundle's visit is the unit, so a per-service quantity has
                          nothing to mean — the server refuses a quantity above 1 on a bundle for exactly this
                          reason (`composition_model_mixed`), and an input that can only be left at 1 is a
                          question with no answer. */}
                      {!isBundleForm && (
                        <>
                          <input
                            type="number"
                            min="1"
                            value={service.quantity}
                            disabled={isValueLocked(editingPackage)}
                            onChange={(e) => updateServiceRow(index, 'quantity', e.target.value)}
                            title="How many sessions of this service the package includes"
                            style={{
                              width: '80px',
                              padding: '12px',
                              borderRadius: '8px',
                              border: '2px solid #e5e7eb',
                              fontSize: '15px'
                            }}
                          />
                          <span
                            style={{
                              alignSelf: 'center',
                              fontSize: '13px',
                              fontWeight: '600',
                              color: '#6b7280',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            sessions
                          </span>
                        </>
                      )}
                      {formData.services.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeServiceRow(index)}
                          style={{
                            padding: '12px 16px',
                            background: '#fee2e2',
                            color: '#dc2626',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600'
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                  {/* A type that covers ONE service cannot grow a second row: the type is what the package IS,
                      and "one service over several visits" with two services is the family type. */}
                  {!oneServiceOnly && (
                    <button
                      type="button"
                      onClick={addServiceRow}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: '#f3f4f6',
                        border: '2px dashed #d1d5db',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        color: '#6b7280',
                        fontSize: '14px'
                      }}
                    >
                      + Add Another Service
                    </button>
                  )}
                </div>

                {/* Actions */}
                {saveError && (
                  <div style={{
                    marginBottom: '16px',
                    padding: '12px',
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '8px',
                    color: '#b91c1c',
                    fontSize: '14px',
                    fontFamily: 'Inter, sans-serif'
                  }}>
                    {saveError}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => { setShowModal(false); setEditingPackage(null); setSaveError(''); }}
                    disabled={saveLoading}
                    style={{
                      padding: '12px 24px',
                      background: '#f3f4f6',
                      color: '#374151',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: saveLoading ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      fontSize: '15px',
                      opacity: saveLoading ? 0.5 : 1
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saveLoading}
                    style={{
                      padding: '12px 32px',
                      background: 'linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: saveLoading ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      fontSize: '15px',
                      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                      opacity: saveLoading ? 0.7 : 1
                    }}
                  >
                    {saveLoading ? 'Saving...' : (editingPackage ? 'Update Package' : 'Create Package')}
                  </button>
                </div>
              </form>
            </div>
          </>
        )}

        {/* Subscribers Details Modal */}
        {showSubscribersModal && selectedPackageForSubscribers && (
          <div 
            onClick={() => setShowSubscribersModal(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '20px'
            }}
          >
            <div 
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: '16px',
                maxWidth: '600px',
                width: '100%',
                maxHeight: '80vh',
                overflow: 'auto',
                padding: '32px',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
              }}
            >
              {/* Modal Header */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '24px', color: '#1e293b' }}>
                  Subscribed Professionals
                </h3>
                <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>
                  {selectedPackageForSubscribers.name}
                </p>
              </div>

              {/* Subscribers List */}
              <div style={{ marginBottom: '20px' }}>
                {selectedPackageForSubscribers.subscriberDetails?.map((subscriber, idx) => (
                  <div 
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '16px',
                      background: idx % 2 === 0 ? '#f9fafb' : 'white',
                      borderRadius: '8px',
                      marginBottom: '8px'
                    }}
                  >
                    <div style={{
                      width: '56px',
                      height: '56px',
                      borderRadius: '50%',
                      background: subscriber.profilePicture 
                        ? `url(${subscriber.profilePicture})` 
                        : 'linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '20px',
                      fontWeight: '600',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}>
                      {!subscriber.profilePicture && subscriber.name?.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                        {subscriber.name}
                      </div>
                      {subscriber.role ? (
                        <div style={{ fontSize: '12px', color: '#64748b' }}>
                          {subscriber.role}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>

              {/* Close Button */}
              <button
                onClick={() => setShowSubscribersModal(false)}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </SidebarLayout>
  );
}

export default Packages;
